import { PublicReview } from '@shared/types';
import { useEffect, useMemo, useState } from 'react';
import { CademyAPI } from '../../../../../../../services/CademyAPI';
import { CademyError } from '@shared/domain-shared';
import {
    ReturnContexts,
    useCourseVisibility,
} from '../../../../../../../hooks/useCourseVisibility';

type Next = null | (() => Promise<void>);

export type Response = [Array<PublicReview>, Next];

const useEducatorReviews = (educatorSlug: string): Response => {
    const [reviews, setReviews] = useState<Array<PublicReview>>([]);
    const [nextPage, setNextPage] = useState<string | undefined>(undefined);
    const appContext = useCourseVisibility();

    useEffect(() => {
        fetchReviews(educatorSlug, undefined, appContext).then((response) => {
            setReviews(response.reviews);
            setNextPage(response.nextPageToken ?? undefined);
        });
    }, [appContext, educatorSlug]);

    const next: Next = useMemo(() => {
        if (nextPage) {
            return () =>
                fetchReviews(educatorSlug, nextPage, appContext).then((response) => {
                    setReviews(reviews.concat(response.reviews));
                    setNextPage(response.nextPageToken ?? undefined);
                });
        }

        return null;
    }, [appContext, educatorSlug, nextPage, reviews]);

    const result = useMemo<Response>(() => [reviews, next], [reviews, next]);
    return result;
};

type FetchReviewsReturn = { reviews: PublicReview[]; nextPageToken?: string };
const fetchReviews = (
    educatorSlug: string,
    nextPage: string | undefined,
    appContext: ReturnContexts
): Promise<FetchReviewsReturn> => {
    return CademyAPI('ListPublicEducatorReviews')({
        urlParams: { educator_slug: educatorSlug },
        queryParams: {
            ...(nextPage ? { page_token: nextPage } : {}),
            context: appContext,
        },
    })
        .then((response): FetchReviewsReturn => {
            if (response.statusCode === 200) {
                return {
                    reviews: response.body?.reviews ?? [],
                    nextPageToken: response.body?.nextPageToken,
                };
            } else {
                throw CademyError.fromUnknown(response.body);
            }
        })
        .catch((err) => {
            console.error('useEducatorReviews fetch error', err);

            return {
                reviews: [],
                nextPageToken: undefined,
            };
        });
};

export default useEducatorReviews;
